import {
  ACCOUNTS_OF_LOGGED_IN_USER_COOKIE_NAME,
  INFINITY_LEARN_COOKIE_NAME,
  PROFILES_OF_LOGGED_IN_USER_COOKIE_NAME,
  type AccountsOfLoggedInUserItem,
  type InfinityLearnCookiePayload,
  type ProfilesOfLoggedInUserPayload,
} from "./types";

export function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function parseJsonCookieValue<T>(raw: string | null): T | null {
  if (!raw?.trim()) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    try {
      return JSON.parse(decodeURIComponent(raw)) as T;
    } catch {
      return null;
    }
  }
}

export function readInfinityLearnCookie(): InfinityLearnCookiePayload | null {
  const parsed = parseJsonCookieValue<InfinityLearnCookiePayload>(
    getCookie(INFINITY_LEARN_COOKIE_NAME),
  );
  if (!parsed?.accessToken?.trim() || !parsed.userDto?.unified_account_id) {
    return null;
  }
  return parsed;
}

export function readAccountsOfLoggedInUserCookie():
  | AccountsOfLoggedInUserItem[]
  | null {
  const parsed = parseJsonCookieValue<AccountsOfLoggedInUserItem[]>(
    getCookie(ACCOUNTS_OF_LOGGED_IN_USER_COOKIE_NAME),
  );
  if (!Array.isArray(parsed) || parsed.length === 0) return null;
  return parsed;
}

export function readProfilesOfLoggedInUserCookie(): ProfilesOfLoggedInUserPayload | null {
  const parsed = parseJsonCookieValue<ProfilesOfLoggedInUserPayload>(
    getCookie(PROFILES_OF_LOGGED_IN_USER_COOKIE_NAME),
  );
  if (
    !parsed?.unified_account_id?.trim() ||
    !Array.isArray(parsed.profiles) ||
    parsed.profiles.length === 0 ||
    !parsed.user_account_details
  ) {
    return null;
  }
  return parsed;
}

function deleteCookieAtPath(name: string, path: string, domain?: string): void {
  const base = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; path=${path}`;
  document.cookie = domain ? `${base}; domain=${domain}` : base;
}

/**
 * Removes IL handoff cookies after session is persisted in {@link useAuthStore}.
 * Tries path=/ and common domain variants so HttpOnly-free cookies set by IL are cleared.
 */
export function clearInfinityLearnHandoffCookies(): void {
  if (typeof document === "undefined") return;

  const names = [
    INFINITY_LEARN_COOKIE_NAME,
    ACCOUNTS_OF_LOGGED_IN_USER_COOKIE_NAME,
    PROFILES_OF_LOGGED_IN_USER_COOKIE_NAME,
  ];

  const hostname = window.location.hostname;
  const domains = new Set<string | undefined>([undefined]);
  domains.add(hostname);
  if (hostname.startsWith(".")) {
    domains.add(hostname.slice(1));
  } else if (hostname.includes(".")) {
    domains.add(`.${hostname}`);
    const parts = hostname.split(".");
    if (parts.length > 2) {
      domains.add(`.${parts.slice(-2).join(".")}`);
    }
  }

  for (const name of names) {
    for (const domain of domains) {
      deleteCookieAtPath(name, "/", domain);
    }
  }
}
